// src/components/HotEventDetail.js
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";

import {
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";

import MapIcon from "@mui/icons-material/Map";
import TimelineIcon from "@mui/icons-material/Timeline";
import { MapContainer, TileLayer, Marker, Polyline, Popup } from "react-leaflet";
import AppHeader from "./AppHeader";

import L from "leaflet";

const redIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const API_BASE =
  process.env.REACT_APP_EMERGENCY_API_URL || "http://localhost:3000/api/emergency";

// eslint-disable-next-line react-hooks/exhaustive-deps
const HotEventDetail = ({ setIsLoggedIn }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [eventData, setEventData] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);

  const formatDateTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      timeZone: "Asia/Yangon",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateTimeShort = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      timeZone: "Asia/Yangon",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);

      const [eventRes, liveRes, locRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/hot/${eventId}`, {
          withCredentials: true,
          headers: { "X-Tenant": localStorage.getItem("tenant") },
        }),
        axios.get(`${API_BASE}/admin/hot/${eventId}/live`, {
          withCredentials: true,
          headers: { "X-Tenant": localStorage.getItem("tenant") },
        }),
        axios.get(`${API_BASE}/admin/hot/${eventId}/locations`, {
          withCredentials: true,
          headers: { "X-Tenant": localStorage.getItem("tenant") },
        }),
      ]);

      if (eventRes.data?.success) {
        setEventData(eventRes.data.event);
      }
      if (liveRes.data?.success) {
        setLiveData(liveRes.data.live);
      }
      if (locRes.data?.success) {
        setLocations(locRes.data.locations || []);
      }
    } catch (err) {
      console.error("Error fetching hot event detail:", err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // initial load
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // live polling for all endpoints (every 10s)
  useEffect(() => {
    const id = setInterval(() => {
      fetchAll();
    }, 10000);
    return () => clearInterval(id);
  }, [fetchAll]);

  const getMapCenter = () => {
    // Prefer live location
    if (liveData?.last_lat && liveData?.last_lng) {
      return [parseFloat(liveData.last_lat), parseFloat(liveData.last_lng)];
    }
    // Then use first historical location
    if (locations.length > 0) {
      const first = locations[0];
      return [parseFloat(first.lat), parseFloat(first.lng)];
    }
    // Default Yangon
    return [16.8409, 96.1735];
  };

  const polylinePositions = locations.map((loc) => [
    parseFloat(loc.lat),
    parseFloat(loc.lng),
  ]);

  if (loading && !eventData) {
    return (
      <Box p={3}>
        <Typography>Loading Hot Event #{eventId}…</Typography>
      </Box>
    );
  }

  if (!eventData) {
    return (
      <Box p={3}>
        <Typography color="error">Hot event not found.</Typography>
        <Button onClick={() => navigate("/emergency/hot")}>Back to list</Button>
      </Box>
    );
  }

  const staffName = eventData.staff_full_name || "";
  const staffCode = eventData.staff_code || "";
  const dept = eventData.department || "";

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Top bar */}
      <AppHeader setIsLoggedIn={setIsLoggedIn} onLogout={() => {
        setIsLoggedIn(false);
        navigate('/login');
      }} />

      {/* Main content */}
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        {/* Left panel – info */}
        <Box sx={{ width: 320, borderRight: "1px solid #eee", p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Participant Info
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2">Staff</Typography>
            <Typography variant="body1">
              {staffName} ({staffCode})
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Department
            </Typography>
            <Typography variant="body2">{dept || "-"}</Typography>

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Event
            </Typography>
            <Typography variant="body2">
              Started: {formatDateTime(eventData.started_at)}
            </Typography>
            <Typography variant="body2">
              Closed: {eventData.closed_at ? formatDateTime(eventData.closed_at) : "-"}
            </Typography>
            <Typography variant="body2">
              Scope: {eventData.scope} / Severity: {eventData.severity}
            </Typography>
          </Paper>

          <Typography variant="h6" gutterBottom>
            Latest Status
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2">
              Participant status: {liveData?.participant_status || "-"}
            </Typography>
            <Typography variant="body2">
              Last source: {liveData?.last_source || "-"}
            </Typography>
            <Typography variant="body2">
              Last accuracy:{" "}
              {liveData?.last_accuracy_m ? `${liveData.last_accuracy_m} m` : "-"}
            </Typography>
            <Typography variant="body2">
              Last reported: {formatDateTime(liveData?.last_reported_at)}
            </Typography>
          </Paper>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<MapIcon />}
              onClick={() => setTab(0)}
              sx={{ mb: 1 }}
            >
              Focus Map
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<TimelineIcon />}
              onClick={() => setTab(1)}
            >
              View History Table
            </Button>
          </Box>
        </Box>

        {/* Right panel – tabs and map/history */}
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab icon={<MapIcon />} label="Live Map & Path" />
            <Tab icon={<TimelineIcon />} label="Location History" />
          </Tabs>

          {/* Tab content */}
          {tab === 0 && (
            <Box sx={{ flexGrow: 1, height: "100%", minHeight: "400px" }}>
              <MapContainer
                center={getMapCenter()}
                zoom={14}
                style={{ height: "100%", width: "100%", minHeight: "400px" }}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {/* polyline of path */}
                {polylinePositions.length > 1 && (
                  <Polyline positions={polylinePositions} />
                )}

                {/* Live marker */}
                {liveData?.last_lat && liveData?.last_lng && (
                  <Marker
                    position={[
                      parseFloat(liveData.last_lat),
                      parseFloat(liveData.last_lng),
                    ]}
                    icon={redIcon}
                  >
                    <Popup>
                      <strong>{staffName}</strong> ({staffCode})
                      <br />
                      Status: {liveData.participant_status}
                      <br />
                      Last: {formatDateTimeShort(liveData.last_reported_at)}
                      <br />
                      Accuracy:{" "}
                      {liveData.last_accuracy_m
                        ? `${liveData.last_accuracy_m} m`
                        : "-"}
                    </Popup>
                  </Marker>
                )}
              </MapContainer>
            </Box>
          )}

          {tab === 1 && (
            <Box sx={{ p: 2, overflowY: "auto" }}>
              <Typography variant="h6" gutterBottom>
                Location History (event #{eventId})
              </Typography>
              {locations.length === 0 ? (
                <Typography variant="body2">No location history recorded.</Typography>
              ) : (
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ borderBottom: "1px solid #ccc", padding: 4 }}>
                        #
                      </th>
                      <th style={{ borderBottom: "1px solid #ccc", padding: 4 }}>
                        Time
                      </th>
                      <th style={{ borderBottom: "1px solid #ccc", padding: 4 }}>
                        Lat
                      </th>
                      <th style={{ borderBottom: "1px solid #ccc", padding: 4 }}>
                        Lng
                      </th>
                      <th style={{ borderBottom: "1px solid #ccc", padding: 4 }}>
                        Accuracy (m)
                      </th>
                      <th style={{ borderBottom: "1px solid #ccc", padding: 4 }}>
                        Source
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map((loc, idx) => (
                      <tr key={loc.id}>
                        <td
                          style={{
                            borderBottom: "1px solid #eee",
                            padding: 4,
                            textAlign: "center",
                          }}
                        >
                          {idx + 1}
                        </td>
                        <td
                          style={{
                            borderBottom: "1px solid #eee",
                            padding: 4,
                          }}
                        >
                          {formatDateTimeShort(loc.recorded_at)}
                        </td>
                        <td
                          style={{
                            borderBottom: "1px solid #eee",
                            padding: 4,
                          }}
                        >
                          {loc.lat}
                        </td>
                        <td
                          style={{
                            borderBottom: "1px solid #eee",
                            padding: 4,
                          }}
                        >
                          {loc.lng}
                        </td>
                        <td
                          style={{
                            borderBottom: "1px solid #eee",
                            padding: 4,
                          }}
                        >
                          {loc.accuracy_m}
                        </td>
                        <td
                          style={{
                            borderBottom: "1px solid #eee",
                            padding: 4,
                          }}
                        >
                          {loc.source}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default HotEventDetail;